import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../utils/api';
import { useNavigate } from 'react-router-dom';
import { uploadToCloudinary } from '../../utils/cloudinary';
import ProtectedAdminRoute from '../../components/ProtectedAdminRoute';

// Removed BACKEND_URL, use apiFetch instead

export default function EditQuantClub() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: 'Quant Club', // Default name
    image: '',
    members: [],
  });
  const [clubId, setClubId] = useState(null);
  const [membersList, setMembersList] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const navigate = useNavigate();

  // Fetch the single Quant Club document on mount
  useEffect(() => {
    const fetchQuantClub = async () => {
      console.log("Attempting to fetch existing Quant Club...");
      try {
        const data = await apiFetch('/api/quant-club/name/Quant%20Club');
        if (data && data._id) {
          setClubId(data._id);
          setForm({
            name: data.name,
            image: data.image,
            members: data.members.map(m => m._id), // Store IDs for submission
          });
          setSelectedMembers(data.members); // Store full member objects for display
          setMessage('Loaded existing Quant Club for editing.');
        } else {
          setMessage('No Quant Club found. You can create one.');
          setClubId(null);
          setForm({ name: 'Quant Club', image: '', members: [] }); // Reset for creation
          setSelectedMembers([]);
        }
      } catch (err) {
        console.error("Network error fetching Quant Club:", err);
        setError(`Network error loading Quant Club: ${err.message}`);
        setClubId(null);
      } finally {
        setLoading(false);
      }
    };

    fetchQuantClub();
  }, []);

  // Fetch all members for assignment
  useEffect(() => {
    apiFetch('/api/members')
      .then(data => setMembersList(Array.isArray(data.members) ? data.members : []))
      .catch((err) => {
        console.error("Error fetching members list:", err);
        setMembersList([]);
      });
  }, []);

  // Filter members by search
  useEffect(() => {
    if (!memberSearch) {
      setFilteredMembers(membersList);
    } else {
      setFilteredMembers(
        membersList.filter(m => m.name.toLowerCase().includes(memberSearch.toLowerCase()))
      );
    }
  }, [memberSearch, membersList]);

  // Add member to selectedMembers
  const handleSelectMember = (member) => {
    if (!selectedMembers.some(m => m._id === member._id)) {
      setSelectedMembers([...selectedMembers, member]);
      setForm(f => ({ ...f, members: [...f.members, member._id] }));
    }
  };

  // Remove member from selectedMembers
  const handleRemoveMember = (id) => {
    setSelectedMembers(selectedMembers.filter(m => m._id !== id));
    setForm(f => ({ ...f, members: f.members.filter(mid => mid !== id) }));
  };

  // Handle image upload
  const handleImageUpload = async e => {
    const file = e.target.files[0];
    if (!file) return;
    setImageUploading(true);
    const previewUrl = URL.createObjectURL(file);
    setForm(f => ({ ...f, image: previewUrl })); // Show local preview immediately
    setMessage('Uploading image...');
    try {
      const result = await uploadToCloudinary(file);
      if (result && result.url) {
        setForm(f => ({ ...f, image: result.url }));
        setMessage('Image uploaded successfully!');
      } else {
        setMessage('Image upload failed: Invalid URL returned.');
        // If upload fails, revert to empty string or default image if desired
        setForm(f => ({ ...f, image: '' })); 
      }
    } catch (err) {
      console.error("Cloudinary upload error:", err);
      setMessage('Image upload failed.');
      setForm(f => ({ ...f, image: '' })); // Clear image on error
    } finally {
      setImageUploading(false);
    }
  };

  // Submit form
  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    if (imageUploading) {
      setMessage('Please wait for the image to finish uploading before saving.');
      setSaving(false);
      return;
    }

    // Determine the image URL to send
    const imageToSend = form.image || "https://example.com/default_quant_club_image.png";

    const dataToSend = {
      name: form.name,
      image: imageToSend,
      members: form.members
    };

    console.log("Attempting to send data:", dataToSend);
    console.log("Current clubId (for POST/PUT decision):", clubId);

    try {
      let data;
      if (clubId) {
        // Update existing
        data = await apiFetch(`/api/quant-club/${clubId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(dataToSend),
        });
      } else {
        // Create new
        data = await apiFetch('/api/quant-club', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(dataToSend),
        });
      }
      if (data && (data._id || data.success)) {
        setMessage('Quant Club saved successfully!');
        if (!clubId && data._id) {
          setClubId(data._id);
        }
        setTimeout(() => navigate('/admin'), 1200);
      } else {
        setMessage(data && (data.message || data.error) ? (data.message || data.error) : 'Save failed.');
      }
    } catch (err) {
      console.error("Fetch/Save operation failed:", err);
      setMessage('Save failed due to network error or unhandled exception: ' + err.message);
    }
    setSaving(false);
  };

  const clubExists = clubId !== null;

  if (loading) return <div className="admin-add-page">Loading...</div>;
  if (error) return <div className="admin-add-page admin-error">{error}</div>;

  return (
    <ProtectedAdminRoute>
      <div className="admin-add-page add-project-group-page">
        <div className="admin-add-header">
          <h2 className="add-project-header">{clubExists ? 'Edit Quant Club' : 'Add Quant Club'}</h2>
          <p className="admin-add-subtitle">
            {clubExists ? 'Edit the existing Quant Club settings.' : 'Add a new Quant Club (only one allowed).'}
          </p>
        </div>
        <form className="admin-add-form" onSubmit={handleSubmit} autoComplete="off" style={{maxWidth: '800px', margin: '20px auto'}}>
          {/* ...existing code... */}
          <div className="form-group">
            <label>Quant Club Name</label>
            <input
              type="text"
              value={form.name}
              className="form-control"
              disabled
              readOnly
            />
          </div>
          {/* ...existing code... */}
          <div className="form-group">
            <label>Quant Club Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="form-control"
              disabled={imageUploading}
            />
            {imageUploading && <div style={{color:'#888',marginTop:4}}>Uploading...</div>}
            {form.image && <img src={form.image} alt="Quant Club" style={{maxWidth:180, borderRadius:8, marginTop:8}} />}
          </div>
          {/* ...existing code... */}
          <div className="form-group">
            <label htmlFor="memberSearch">Add Members</label>
            <input
              id="memberSearch"
              type="text"
              value={memberSearch}
              onChange={e => setMemberSearch(e.target.value)}
              placeholder="Search members by name"
              className="form-control"
              disabled={!membersList.length} // Disable if no members to search
            />
            <div className="member-search-results">
              {filteredMembers.slice(0, 8).map(member => (
                <div
                  key={member._id}
                  className={`member-search-item${selectedMembers.some(m => m._id === member._id) ? ' selected' : ''}`}
                  onClick={() => handleSelectMember(member)}
                  style={{ cursor: 'pointer', opacity: selectedMembers.some(m => m._id === member._id) ? 0.5 : 1 }}
                >
                  {member.name} {member.email && <span className="member-email">({member.email})</span>}
                </div>
              ))}
              {memberSearch && filteredMembers.length === 0 && <div className="no-results">No members found.</div>}
              {!memberSearch && membersList.length === 0 && <div className="no-results">No members available to add.</div>}
            </div>
          </div>
          {/* ...existing code... */}
          <div className="form-group selected-members">
            <label>Selected Members</label>
            <div className="selected-members-list">
              {selectedMembers.map(member => (
                <div key={member._id} className="selected-member-card">
                  <span>{member.name}</span>
                  <button type="button" onClick={() => handleRemoveMember(member._id)}>&times;</button>
                </div>
              ))}
              {selectedMembers.length === 0 && <span className="no-members">No members selected</span>}
            </div>
          </div>
          {/* ...existing code... */}
          <button type="submit" disabled={saving || imageUploading} className="action-btn primary">
            {saving ? (clubExists ? 'Updating...' : 'Creating...') : (clubExists ? 'Save Changes' : 'Add Quant Club')}
          </button>
          {message && <div className={`message ${message.startsWith('Quant Club saved') ? 'success-msg' : 'error-msg'}`}>{message}</div>}
        </form>
      </div>
    </ProtectedAdminRoute>
  );
}